/* eslint-disable */
// modules
import UserModule from "@/store/modules/user";

// helpers
import {
  RequestAppSearch,
  EngineTypes,
  EngineKeys,
} from "@/app-search/services/request-app-search.service";
import POSConfigModule from "@/store/modules/pos-config";
import {
  RequestElasticsearch,
  ElasticEnabledGroups,
  EngineTypeElasticEnabled,
} from "@/app-search/services/request-elasticsearch.service";
import { ElasticsearchAccessError } from "@/constants/errors/elasticsearch";

export interface IFilter {
  query: "";
  options: {
    page: { size: number; current: number };
    filters: any;
    result_fields?: any;
    search_fields?: any;
  };
}

/**
 * Get app search data
 *
 * @param {EngineTypes} engineType: engine type
 * @param {EngineKeys} engineKey: engine key
 * @param {object} payload: params for request
 * @returns {Promise<object[]>}
 */
export async function query(
  engineType: EngineTypes,
  engineKey: EngineKeys,
  payload: any
): Promise<any> {
  const { client, isElastic } = createSearchClient(engineType, engineKey);

  // L3-2802-newpos-pull-only-orders-of-the-selected-group-in-orders-table
  // If the user is an admin we consider the gid parameter value
  // const userGroups = UserModule.getUserGroups.map((u) => String(u.group.id));
  // const groupIds = UserModule.isAdmin ? [String(UserModule.getUserGroupId)] : userGroups;

  const groupId = [
    isElastic ? POSConfigModule.groupId : String(POSConfigModule.groupId),
  ];

  const params = {
    query: payload.query,
    options: {
      ...payload.options,
      filters: {
        ...payload.options.filters,
        all: [
          { group_access: groupId },
          ...(payload.options.filters.all || []),
        ],
      },
    },
  };

  const response = await client.send(params);

  return response;
}

/**
 * Returns searchKey and client depending on engine type
 *
 * @param {EngineTypes} engineType: engine type
 * @param {EngineKeys} engineKey: engine key
 * @returns {{ searchKey: string, client: any; isElastic: boolean }}
 */
function createSearchClient(
  engineType: EngineTypes,
  engineKey: EngineKeys
): { searchKey: string; client: any; isElastic: boolean } {
  // Determine engine type: 0 = always AppSearch, 1 = always Elasticsearch
  const isElastic = isElasticEngine(engineType);

  let searchKey: string;
  let client: any;

  if (isElastic) {
    searchKey = UserModule?.getUser?.elasticSearchTokens?.[engineKey];
    if (!searchKey) {
      throw new ElasticsearchAccessError(
        `Missing Elasticsearch token for engine "${engineKey}". User may lack sufficient permissions.`
      );
    }
    client = new RequestElasticsearch({ engineName: engineType, searchKey });
  } else {
    searchKey = UserModule?.getUser?.tableSearchKeys[engineKey];
    client = new RequestAppSearch({ engineName: engineType, searchKey });
  }

  return { searchKey, client, isElastic };
}

/**
 * Determines if the current search engine mode is Elasticsearch for the given engineType
 * Returns true if Elasticsearch should be used, false for AppSearch
 *
 * @param {EngineTypes} engineType: the AppSearch engine type
 * @returns {boolean}
 */
export function isElasticEngine(engineType: EngineTypes): boolean {
  // determine engine type: 0 = always AppSearch, 1 = always Elasticsearch, 2 = per-engine config
  const envEngineType = import.meta.env.VITE_SEARCH_ENGINE_TYPE;
  const isProd = import.meta.env.VITE_ENV === "prod";
  const hasGroup = ElasticEnabledGroups.includes(
    Number(POSConfigModule.groupId)
  );

  if (envEngineType === "1") {
    return true;
  }

  if (envEngineType === "2") {
    // in prod: check group and engine; in dev/test: only engine config
    return isProd
      ? hasGroup && EngineTypeElasticEnabled[engineType]
      : EngineTypeElasticEnabled[engineType];
  }

  return false;
}
